import type { WaitlistFormFieldErrors, WaitlistSurveyState } from "@/types/waitlist";
import { validateEmail, validateName, validateOrganizationName } from "@/utils/authValidation";
import { isValidPhoneNumber } from "react-phone-number-input";

export function initialWaitlistSurvey(): WaitlistSurveyState {
  return {
    primaryPracticeArea: "",
    reportsPerMonth: "",
    pagesMin: "",
    pagesMax: "",
    caseFlowConsistent: null,
  };
}

export function collectSurveyErrors(survey: WaitlistSurveyState): Partial<WaitlistFormFieldErrors> {
  const e: Partial<WaitlistFormFieldErrors> = {};
  if (!survey.primaryPracticeArea) {
    e.primaryPracticeArea = "Please select an option";
  }
  const reports = parseInt(survey.reportsPerMonth, 10);
  if (!survey.reportsPerMonth.trim() || Number.isNaN(reports) || reports < 1 || reports > 500_000) {
    e.reportsPerMonth = "Enter a number between 1 and 500,000";
  }
  const min = parseInt(survey.pagesMin, 10);
  const max = parseInt(survey.pagesMax, 10);
  if (
    [survey.pagesMin, survey.pagesMax].some((x) => !String(x).trim()) ||
    Number.isNaN(min) ||
    Number.isNaN(max)
  ) {
    e.pages = "Enter min and max pages";
  } else if (min < 1 || max < 1) {
    e.pages = "Min and max must be at least 1";
  } else if (min > max) {
    e.pages = "Min cannot be greater than max";
  }
  if (survey.caseFlowConsistent === null) {
    e.caseFlow = "Please select an option";
  }
  return e;
}

export function getWaitlistStep1Errors(params: {
  email: string;
  name: string;
  organizationName: string;
  contactPhone: string;
}): Partial<Pick<WaitlistFormFieldErrors, "email" | "name" | "organizationName" | "contactPhone">> {
  const e: Partial<WaitlistFormFieldErrors> = {};
  const emailError = validateEmail(params.email);
  if (emailError) e.email = emailError;
  const nameError = validateName(params.name);
  if (nameError) e.name = nameError;
  const orgError = validateOrganizationName(params.organizationName);
  if (orgError) e.organizationName = orgError;
  if (!params.contactPhone || !isValidPhoneNumber(params.contactPhone)) {
    e.contactPhone = "Enter a valid phone number";
  }
  return e;
}

export function getWaitlistStep3Errors(params: {
  isProfessional: boolean;
  specialtyId: string;
}): Partial<Pick<WaitlistFormFieldErrors, "specialty">> {
  const e: Partial<WaitlistFormFieldErrors> = {};
  if (params.isProfessional && !params.specialtyId) {
    e.specialty = "Please select a specialty";
  }
  return e;
}

/** Full form validation: steps 1–3 (contact, survey, professional). Field keys do not overlap. */
export function getWaitlistAllStepsErrors(params: {
  email: string;
  name: string;
  organizationName: string;
  contactPhone: string;
  survey: WaitlistSurveyState;
  isProfessional: boolean;
  specialtyId: string;
}): Partial<WaitlistFormFieldErrors> {
  return {
    ...getWaitlistStep1Errors({
      email: params.email,
      name: params.name,
      organizationName: params.organizationName,
      contactPhone: params.contactPhone,
    }),
    ...collectSurveyErrors(params.survey),
    ...getWaitlistStep3Errors({
      isProfessional: params.isProfessional,
      specialtyId: params.specialtyId,
    }),
  };
}
